import { Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { FalModelPicker } from "@/features/image/FalModelPicker";
import {
  falModelOptions,
  formatDuration,
  type ImageGenerationResult,
} from "@/lib/playground";

export type ImagePanelState = {
  id: string;
  model: string;
  status: "idle" | "loading" | "done" | "error";
  result: ImageGenerationResult | null;
  error: string | null;
};

type ImageColumnProps = {
  panel: ImagePanelState;
  onModelChange: (model: string) => void;
  onRemove: () => void;
  canRemove: boolean;
};

export const ImageColumn = ({
  panel,
  onModelChange,
  onRemove,
  canRemove,
}: ImageColumnProps) => (
  <Card className="relative flex flex-col border-0 border-b border-r after:absolute after:left-0 after:top-0 after:z-[1] after:h-[2px] after:w-0 after:bg-primary after:transition-[width] after:duration-300 after:content-[''] hover:after:w-full">
    <CardHeader className="flex flex-row items-center gap-1 space-y-0 border-b p-2">
      <FalModelPicker
        value={panel.model}
        onChange={onModelChange}
        disabled={panel.status === "loading"}
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        disabled={!canRemove || panel.status === "loading"}
        aria-label="Remove model"
        className="h-8 w-8 px-0 text-subtle hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </Button>
    </CardHeader>

    <CardContent className="flex min-h-[320px] flex-1 flex-col items-center justify-center gap-3 p-4">
      {panel.status === "loading" ? (
        <div className="flex flex-col items-center gap-3 font-mono text-xs uppercase tracking-[0.8px] text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          Generating
        </div>
      ) : null}

      {panel.status === "error" && panel.error ? (
        <p className="w-full border border-primary bg-primary/5 px-3 py-2 text-sm text-primary">
          {panel.error}
        </p>
      ) : null}

      {panel.status === "done" && panel.result ? (
        panel.result.mocked ? (
          <p className="w-full border border-primary bg-primary/5 px-3 py-2 text-sm text-primary">
            {panel.result.notice}
          </p>
        ) : (
          <div className="w-full space-y-2">
            {panel.result.images.map((image) => (
              <a
                key={image.url}
                href={image.url}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={image.url}
                  alt={`Generated by ${panel.model}`}
                  className="w-full border object-contain"
                />
              </a>
            ))}
          </div>
        )
      ) : null}

      {panel.status === "idle" ? (
        <p className="font-mono text-[11px] uppercase tracking-[0.8px] text-subtle">
          [ awaiting first run ]
        </p>
      ) : null}
    </CardContent>

    <div className="flex items-center gap-2 border-t px-4 py-2 font-mono text-[10px] uppercase tracking-[0.8px] text-subtle">
      {panel.status === "loading" ? (
        <>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
          Generating
        </>
      ) : panel.result && !panel.result.mocked ? (
        `${formatDuration(panel.result.durationMs)} · ${
          falModelOptions.find((option) => option.id === panel.model)?.label ??
          panel.model
        }`
      ) : (
        "— no run yet"
      )}
    </div>
  </Card>
);
